import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Check, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pricing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [myBizs, setMyBizs] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [busy, setBusy] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [interval] = useState("monthly"); // annual retired — see PRD 2026-06-30

  useEffect(() => {
    api.get("/paypal/plans").then((r) => setPlans(r.data)).catch(() => {});
    if (user) {
      api.get("/dashboard/businesses").then((r) => {
        setMyBizs(r.data);
        if (r.data[0]) setSelectedSlug(r.data[0].slug);
      });
    }
  }, [user]);

  const subscribe = async (tier) => {
    if (!user) return navigate("/connexion?redirect=/passez-partenaire");
    if (!selectedSlug) return toast.error(t.pricing.need_claim);
    setBusy(`${tier}_${interval}`);
    try {
      const r = await api.post("/paypal/subscriptions/create", { tier, interval, business_slug: selectedSlug });
      if (r.data.approve_url) window.location.href = r.data.approve_url;
      else toast.error("Pas d'URL d'approbation reçue");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur PayPal");
    } finally {
      setBusy(null);
    }
  };

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    setPromoInfo(null);
    try {
      const r = await api.post("/promo/validate", { code: promoCode });
      setPromoInfo(r.data);
      toast.success("Code promo valide !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally {
      setPromoBusy(false);
    }
  };

  const redeemPromo = async () => {
    if (!user) return navigate("/connexion?redirect=/passez-partenaire");
    if (!selectedSlug) return toast.error("Sélectionnez d'abord votre entreprise ci-dessus.");
    setPromoBusy(true);
    try {
      const r = await api.post("/promo/redeem", { code: promoCode, business_slug: selectedSlug });
      toast.success(r.data.message || "Premium activé !");
      setTimeout(() => navigate(`/dashboard?slug=${selectedSlug}&promo=1`), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setPromoBusy(false);
    }
  };

  const startTrial = async () => {
    if (!user) return navigate("/connexion?redirect=/passez-partenaire");
    if (!selectedSlug) return toast.error("Réclamez d'abord une fiche pour démarrer l'essai.");
    setPromoBusy(true);
    try {
      const r = await api.post("/dashboard/trial/start", { business_slug: selectedSlug });
      toast.success(r.data.message || "Essai gratuit 14 jours activé !");
      setTimeout(() => navigate(`/dashboard?slug=${selectedSlug}&trial=1`), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setPromoBusy(false);
    }
  };

  const isPromoActive = plans?.promo_status?.is_promo_active ?? true;
  const remainingSeats = plans?.promo_status?.remaining_seats ?? null;
  const totalSeats = plans?.promo_status?.total_seats ?? 100;
  const variant = isPromoActive ? "promo" : "standard";

  const activeBiz = myBizs.find((b) => b.slug === selectedSlug);
  const canStartTrial = !!activeBiz
    && !activeBiz.trial_used
    && (activeBiz.premium?.tier || "free") === "free";
  const currentTier = activeBiz?.premium?.tier || "free";
  const isAlreadyPremium = currentTier !== "free";
  const alreadyPremiumAI = currentTier === "premium_ai";
  const expiresAt = activeBiz?.premium?.expires_at;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-20" data-testid="pricing-page">
      <div className="max-w-3xl">
        <div className="label-uppercase mb-3">{t.pricing.kicker}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium leading-tight">{t.pricing.h1}</h1>
      </div>

      {user && myBizs.length > 0 && (
        <div className="mt-10 bg-card border border-border rounded-sm p-5 max-w-xl">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Pour quelle entreprise ?</div>
          <select value={selectedSlug} onChange={(e) => setSelectedSlug(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background mt-1" data-testid="pricing-business-select">
            {myBizs.map((b) => <option key={b.slug} value={b.slug}>{b.name} — {b.city}</option>)}
          </select>
        </div>
      )}

      {user && myBizs.length === 0 && (
        <div
          className="mt-10 bg-accent/40 border border-border rounded-sm p-6 max-w-2xl"
          data-testid="pricing-need-claim-msg"
        >
          <div className="font-display text-lg font-medium text-foreground mb-1">
            Il vous manque une étape&nbsp;: réclamer votre fiche
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {t.pricing.need_claim} C'est gratuit, immédiat, et sans engagement — vous pourrez ensuite
            activer vos <b>14 jours Premium AI offerts</b>.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/annuaire"
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] px-4 py-2 rounded-sm text-sm font-semibold transition-colors"
              data-testid="pricing-browse-directory-btn"
            >
              Chercher mon entreprise <ArrowRight size={12} />
            </Link>
            <a
              href="mailto:moncommerce68@gmail.com?subject=Ajout de mon entreprise à l'annuaire&body=Bonjour,%0D%0A%0D%0AJe souhaite être ajouté à l'annuaire. Voici mes coordonnées :%0D%0A- Nom de l'entreprise :%0D%0A- Ville :%0D%0A- Catégorie :%0D%0A- Téléphone :%0D%0A%0D%0AMerci."
              className="inline-flex items-center gap-1.5 bg-background border border-border hover:border-primary hover:text-primary px-4 py-2 rounded-sm text-sm font-semibold transition-colors"
              data-testid="pricing-add-business-btn"
            >
              Je ne trouve pas ma fiche
            </a>
          </div>
        </div>
      )}

      {/* Free trial CTA */}
      {user && canStartTrial && (
        <div
          className="mt-10 relative overflow-hidden bg-gradient-to-br from-primary to-[#0f3d29] text-primary-foreground rounded-sm p-6 md:p-8 max-w-3xl"
          data-testid="trial-block"
        >
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-secondary/30 rounded-full blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm mb-3">
                <Sparkle size={10} weight="fill" /> SANS CARTE BANCAIRE
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-2">
                14 jours de Premium AI offerts.
              </h3>
              <p className="text-sm opacity-90 leading-relaxed max-w-lg">
                Toutes les fonctionnalités débloquées immédiatement : badge partenaire, boutons contact, photos, IA posts, rapport mensuel, flyer PDF, demand-forecast. Aucun engagement, aucune carte demandée.
              </p>
            </div>
            <button
              onClick={startTrial}
              disabled={promoBusy}
              className="shrink-0 bg-white text-primary hover:bg-[#F9F8F6] px-6 py-3.5 rounded-sm font-semibold text-sm shadow-lg disabled:opacity-60 flex items-center gap-2"
              data-testid="trial-start-btn"
            >
              {promoBusy ? "…" : t.pricing.start_trial_cta} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {user && activeBiz?.trial_used && (activeBiz.premium?.tier || "free") === "free" && (
        <div className="mt-10 bg-accent/40 border border-border rounded-sm p-5 max-w-3xl text-sm" data-testid="trial-used-msg">
          <b>Essai gratuit déjà utilisé</b> sur cette fiche. Passez Premium pour réactiver les outils ci-dessous.
        </div>
      )}

      <div className="mt-12 max-w-4xl">
        {/* Promo banner for first-year -50% — only shown while promo seats remain */}
        {isPromoActive ? (
          <div className="text-center mb-6" data-testid="promo-banner">
            <div className="text-xs font-semibold text-secondary uppercase tracking-[0.15em] flex items-center justify-center gap-2">
              <Sparkle size={12} weight="fill"/> {t.pricing.promo_banner}
              <Sparkle size={12} weight="fill"/>
            </div>
            {remainingSeats != null && (
              <div className="mt-2 inline-flex items-center gap-2 bg-secondary/10 border border-secondary/30 text-secondary text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm" data-testid="promo-seats-remaining">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                Plus que <b className="text-base">{remainingSeats}</b> / {totalSeats} places à -50%
              </div>
            )}
          </div>
        ) : (
          <div className="text-center mb-6 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider" data-testid="promo-ended-banner">
            Offre lancement terminée — tarif standard appliqué
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <PlanCard
            t={t}
            name={t.pricing.premium_name}
            plan={plans?.plans?.[`premium_${interval}_${variant}`]}
            interval={interval}
            isPromoActive={isPromoActive}
            features={t.pricing.features_premium}
            cta={alreadyPremiumAI ? "Vous avez déjà Premium AI" : currentTier === "premium" ? "Abonnement actif ✓" : busy === `premium_${interval}` ? "…" : t.pricing.cta}
            onSubscribe={() => subscribe("premium")}
            testid="plan-premium"
            disabled={busy !== null || !user || isAlreadyPremium}
          />
          <PlanCard
            t={t}
            name={t.pricing.premium_name + " IA"}
            badge={t.pricing.most_popular}
            plan={plans?.plans?.[`premium_ai_${interval}_${variant}`]}
            interval={interval}
            isPromoActive={isPromoActive}
            features={t.pricing.features_premium_ai}
            cta={alreadyPremiumAI ? "Abonnement actif ✓" : busy === `premium_ai_${interval}` ? "…" : t.pricing.cta}
            onSubscribe={() => subscribe("premium_ai")}
            highlighted
            testid="plan-premium-ai"
            disabled={busy !== null || !user || alreadyPremiumAI}
          />
        </div>

        {isAlreadyPremium && (
          <div className="mt-6 rounded-sm border border-primary/20 bg-primary/5 px-4 py-3 text-sm" data-testid="already-premium-notice">
            <span className="font-semibold text-primary">✓ Abonnement {currentTier === "premium_ai" ? "Premium AI" : "Premium"} actif</span>
            {expiresAt && <span className="text-muted-foreground ml-2">· Valable jusqu'au {new Date(expiresAt).toLocaleDateString("fr-FR")}</span>}
            <div className="mt-1 text-xs text-muted-foreground">Pour gérer ou annuler, rendez-vous dans votre <a href="/dashboard" className="underline font-semibold">espace partenaire</a>.</div>
          </div>
        )}
      </div>

      {!user && (
        <div className="mt-8 text-sm text-muted-foreground">
          {t.pricing.need_login} <a href="/connexion" className="text-primary font-semibold underline">→ {t.nav.login}</a>
        </div>
      )}

      {/* Promo code */}
      {user && (
        <div className="mt-16 max-w-2xl bg-card border border-border rounded-sm p-6" data-testid="promo-block">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={16} weight="fill" className="text-secondary" />
            <div className="font-display text-lg font-medium">Vous avez un code promo ?</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Entrez votre code pour activer vos avantages sans passer par PayPal.</p>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoInfo(null); }}
              placeholder="Saisissez votre code"
              className="flex-1 min-w-[200px] border border-border rounded-sm px-3 py-2.5 text-sm bg-background uppercase tracking-wider font-mono outline-none focus:border-primary"
              data-testid="promo-input"
              disabled={promoBusy}
            />
            {!promoInfo ? (
              <button
                onClick={validatePromo}
                disabled={!promoCode.trim() || promoBusy}
                className="px-5 py-2.5 border border-foreground text-sm font-semibold rounded-sm hover:bg-foreground hover:text-background disabled:opacity-50"
                data-testid="promo-validate"
              >
                {promoBusy ? "…" : "Valider"}
              </button>
            ) : (
              <button
                onClick={redeemPromo}
                disabled={promoBusy || !selectedSlug}
                className="px-5 py-2.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white font-semibold rounded-sm text-sm disabled:opacity-50"
                data-testid="promo-redeem"
              >
                {promoBusy ? "…" : "Activer Premium AI"}
              </button>
            )}
          </div>
          {promoInfo && (
            <div className="mt-4 bg-primary/5 border border-primary/30 rounded-sm p-3 text-sm" data-testid="promo-info">
              <div className="font-semibold text-primary mb-0.5">✓ {promoInfo.label || promoInfo.code}</div>
              <div className="text-xs text-muted-foreground">
                Active <b>{promoInfo.tier === "premium_ai" ? "Premium AI" : "Premium"}</b> pendant <b>{promoInfo.grant_months} mois</b>.
                {!selectedSlug && <span className="block mt-1 text-orange-700">⚠ Sélectionnez d'abord l'entreprise ci-dessus.</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAQ */}
      <div className="mt-24 max-w-3xl">
        <h2 className="font-display text-3xl tracking-tight font-medium mb-8">{t.pricing.faq_h2}</h2>
        <div className="divide-y divide-border border-y border-border">
          {[
            {q: "Suis-je engagé sur la durée ?", a: "Non. Aucun engagement, vous résiliez en un clic à tout moment depuis votre espace partenaire."},
            {q: "Comment puis-je annuler ?", a: "Depuis votre espace partenaire ou directement dans votre compte PayPal. L'annulation prend effet à la fin de la période en cours."},
            {q: "Mes informations sont-elles sécurisées ?", a: "Oui — paiement géré par PayPal, données hébergées en Europe, conformité RGPD complète."},
            {q: "Puis-je réclamer une fiche déjà existante ?", a: "Oui. Trouvez votre entreprise dans l'annuaire et cliquez sur \"Réclamer cette fiche\"."},
          ].map((f, i) => (
            <details key={f.q} className="py-5 group" data-testid={`faq-${i}`}>
              <summary className="flex justify-between items-center cursor-pointer list-none">
                <span className="font-display text-lg font-medium">{f.q}</span>
                <span className="text-primary text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ t, name, plan, interval, isPromoActive, features, cta, onSubscribe, highlighted, badge, testid, disabled }) {
  // Fallback values while `/paypal/plans` request is in flight — avoids a skeleton flicker
  const regular = plan?.regular_price ? Number(plan.regular_price) : null;
  const promo = plan?.promo_price ? Number(plan.promo_price) : null;
  const per = interval === "annual" ? t.pricing.per_year : t.pricing.per_month;
  const promoLabel = interval === "annual" ? t.pricing.promo_first_year : t.pricing.promo_first_months;
  const fmt = (n) => n != null ? n.toFixed(2).replace(".", ",") + " €" : "—";
  return (
    <div className="relative" data-testid={testid}>
      {badge && (
        <div className="absolute -top-3 left-6 z-10 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm flex items-center gap-1 shadow-[0_8px_24px_-8px_rgba(200,121,90,0.5)]">
          <Sparkle size={10} weight="fill"/> {badge}
        </div>
      )}
      <div className={`relative bg-card border ${highlighted ? "border-primary beam-border" : "border-border"} rounded-sm p-8 flex flex-col h-full`}>
        <div className="font-display text-2xl font-medium mb-1">{name}</div>

        {/* Pricing — when promo is active emphasize the discounted price + show 'puis X€';
            when promo seats are exhausted, show only the regular price. */}
        {isPromoActive ? (
          <>
            <div className="mb-1" data-testid={`${testid}-price`}>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-medium tracking-tight text-secondary">{fmt(promo)}</span>
                <span className="text-sm text-muted-foreground">{per}</span>
              </div>
              <div className="text-[11px] text-secondary font-bold uppercase tracking-wider mt-1">
                {promoLabel}
              </div>
            </div>
            <div className="flex items-baseline gap-1 mb-6 mt-2 text-xs text-muted-foreground" data-testid={`${testid}-regular-price`}>
              {t.pricing.price_then} <b className="text-foreground font-semibold">{fmt(regular)}</b> {per}
              {interval === "annual" && regular && (
                <span className="ml-1">
                  ({t.pricing.equivalent_monthly.replace("{price}", (regular / 12).toFixed(2).replace(".", ",") + " €")})
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="mb-6" data-testid={`${testid}-price`}>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl font-medium tracking-tight text-foreground">{fmt(regular)}</span>
              <span className="text-sm text-muted-foreground">{per}</span>
            </div>
            {interval === "annual" && regular && (
              <div className="text-xs text-muted-foreground mt-1">
                {t.pricing.equivalent_monthly.replace("{price}", (regular / 12).toFixed(2).replace(".", ",") + " €")}
              </div>
            )}
          </div>
        )}

        <ul className="space-y-2.5 mb-8">
          {features.map((f, i) => (
            <li key={`${i}-${String(f).slice(0, 24)}`} className="flex items-start gap-2 text-sm">
              <Check weight="bold" size={16} className="text-primary mt-0.5 shrink-0"/> <span>{f}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={onSubscribe}
          disabled={disabled || !plan?.id}
          className={`mt-auto w-full px-5 py-3 text-sm font-semibold rounded-sm flex items-center justify-center gap-2 transition-colors ${
            highlighted ? "bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white" : "border border-foreground hover:bg-foreground hover:text-background"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid={`${testid}-subscribe`}
        >
          {cta} <ArrowRight size={14}/>
        </button>
      </div>
    </div>
  );
}
